import {
  createPackage as publicCreatePackage,
  createPackageV2 as publicCreatePackageV2,
  cancelPackage as publicCancelPackage,
  getPackage as publicGetPackage,
  queryPackages as publicQueryPackages,
  typedQueryPackages as publicTypedQueryPackages,
  assignProductInstanceToSite as publicAssignProductInstanceToSite,
  unassignProductInstanceFromSite as publicUnassignProductInstanceFromSite,
  cancelProductInstance as publicCancelProductInstance,
  adjustProductInstanceSpecifications as publicAdjustProductInstanceSpecifications,
  updatePackageExternalId as publicUpdatePackageExternalId,
} from './premium-reseller-v1-packages-packages.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { BuildRESTFunction, MaybeContext } from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  PackageQuery,
  PackagesQueryBuilder,
  typedQueryPackages as universalTypedQueryPackages,
} from './premium-reseller-v1-packages-packages.universal.js';

function customQueryPackages(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: () => publicQueryPackages(httpClient)(),
    typedQueryFunction: (query: PackageQuery) =>
      publicTypedQueryPackages(httpClient)(query),
    hasOptionsParameter: false,
  });

  function overloadedQuery(): PackagesQueryBuilder;
  function overloadedQuery(
    query: PackageQuery
  ): ReturnType<typeof universalTypedQueryPackages>;
  function overloadedQuery(query?: PackageQuery): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const createPackage: MaybeContext<
  BuildRESTFunction<typeof publicCreatePackage> & typeof publicCreatePackage
> = /*#__PURE__*/ createRESTModule(publicCreatePackage);
export const createPackageV2: MaybeContext<
  BuildRESTFunction<typeof publicCreatePackageV2> & typeof publicCreatePackageV2
> = /*#__PURE__*/ createRESTModule(publicCreatePackageV2);
export const cancelPackage: MaybeContext<
  BuildRESTFunction<typeof publicCancelPackage> & typeof publicCancelPackage
> = /*#__PURE__*/ createRESTModule(publicCancelPackage);
export const getPackage: MaybeContext<
  BuildRESTFunction<typeof publicGetPackage> & typeof publicGetPackage
> = /*#__PURE__*/ createRESTModule(publicGetPackage);
export const assignProductInstanceToSite: MaybeContext<
  BuildRESTFunction<typeof publicAssignProductInstanceToSite> &
    typeof publicAssignProductInstanceToSite
> = /*#__PURE__*/ createRESTModule(publicAssignProductInstanceToSite);
export const unassignProductInstanceFromSite: MaybeContext<
  BuildRESTFunction<typeof publicUnassignProductInstanceFromSite> &
    typeof publicUnassignProductInstanceFromSite
> = /*#__PURE__*/ createRESTModule(publicUnassignProductInstanceFromSite);
export const cancelProductInstance: MaybeContext<
  BuildRESTFunction<typeof publicCancelProductInstance> &
    typeof publicCancelProductInstance
> = /*#__PURE__*/ createRESTModule(publicCancelProductInstance);
export const adjustProductInstanceSpecifications: MaybeContext<
  BuildRESTFunction<typeof publicAdjustProductInstanceSpecifications> &
    typeof publicAdjustProductInstanceSpecifications
> = /*#__PURE__*/ createRESTModule(publicAdjustProductInstanceSpecifications);
export const updatePackageExternalId: MaybeContext<
  BuildRESTFunction<typeof publicUpdatePackageExternalId> &
    typeof publicUpdatePackageExternalId
> = /*#__PURE__*/ createRESTModule(publicUpdatePackageExternalId);
export const queryPackages: MaybeContext<
  BuildRESTFunction<typeof customQueryPackages> & typeof customQueryPackages
> = /*#__PURE__*/ createRESTModule(customQueryPackages);

export {
  FailureReasonCode,
  IntervalIntervalUnit,
  CycleDescriptorType,
  Status,
  SortOrder,
  ProviderName,
  IntervalUnit,
  SubscriptionStatus,
  ReactivationReasonEnum,
  Initiator,
  RefundType,
  UnassignReason,
  ContractSwitchType,
  ContractSwitchReason,
  PriceIncreaseTrigger,
  ProductAdjustment,
  FieldToUpdate,
  WebhookIdentityType,
} from './premium-reseller-v1-packages-packages.universal.js';
export {
  Package,
  ProductInstance,
  ProductInstanceContractDetailsOneOf,
  CycleInterval,
  FailureReason,
  ProductInstanceCycle,
  CreatePackageRequest,
  CreatePackageResponse,
  CancelPackageRequest,
  CancelPackageResponse,
  PackageCanceled,
  GetPackageRequest,
  GetPackageResponse,
  QueryPackagesRequest,
  QueryV2,
  QueryV2PagingMethodOneOf,
  Sorting,
  Paging,
  CursorPaging,
  QueryPackagesResponse,
  PagingMetadataV2,
  Cursors,
  AssignProductInstanceToSiteRequest,
  AssignProductInstanceToSiteResponse,
  AssignedProductInstanceToSite,
  UnassignProductInstanceFromSiteRequest,
  UnassignProductInstanceFromSiteResponse,
  UnassignedProductInstanceFromSite,
  CancelProductInstanceRequest,
  CancelProductInstanceResponse,
  ProductInstanceCanceled,
  AdjustProductInstanceSpecificationsRequest,
  AdjustProductInstanceSpecificationsResponse,
  AdjustedProductInstanceSpecifications,
  SubscriptionEvent,
  SubscriptionEventEventOneOf,
  SubscriptionCreated,
  Subscription,
  BillingReference,
  Cycle,
  CycleCycleSelectorOneOf,
  Interval,
  OneTime,
  ReactivationData,
  SubscriptionAssigned,
  SubscriptionCancelled,
  CancellationDetails,
  SubscriptionAutoRenewTurnedOn,
  SubscriptionAutoRenewTurnedOff,
  SubscriptionUnassigned,
  SubscriptionTransferred,
  RecurringChargeSucceeded,
  ContractSwitched,
  ProductPriceIncreaseData,
  SubscriptionNearEndOfPeriod,
  SubscriptionPendingChange,
  RecurringChargeAttemptFailed,
  Empty,
  ProductInstanceUpdated,
  FailureReportRequest,
  ProductInstanceFailed,
  UpdateProductInstanceRequest,
  UpdateProductInstanceResponse,
  UpdatePackageExternalIdRequest,
  UpdatePackageExternalIdResponse,
  CountPackagesRequest,
  CountPackagesResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  MigrateSubscriptionToPackagesRequest,
  MigrateSubscriptionToPackagesResponse,
  FixBrokenMigrationRequest,
  FixBrokenMigrationResponse,
  CreatePackageOptions,
  CreatePackageV2Options,
  CancelPackageOptions,
  PackagesQueryResult,
  PackagesQueryBuilder,
  PackageQuerySpec,
  AssignProductInstanceToSiteIdentifiers,
  AssignProductInstanceToSiteOptions,
  UnassignProductInstanceFromSiteOptions,
  CancelProductInstanceOptions,
  AdjustProductInstanceSpecificationsOptions,
  UpdatePackageExternalIdIdentifiers,
} from './premium-reseller-v1-packages-packages.universal.js';
export { utils } from './premium-reseller-v1-packages-packages.universal.js';
export {
  FailureReasonCodeWithLiterals,
  IntervalIntervalUnitWithLiterals,
  CycleDescriptorTypeWithLiterals,
  StatusWithLiterals,
  SortOrderWithLiterals,
  ProviderNameWithLiterals,
  IntervalUnitWithLiterals,
  SubscriptionStatusWithLiterals,
  ReactivationReasonEnumWithLiterals,
  InitiatorWithLiterals,
  RefundTypeWithLiterals,
  UnassignReasonWithLiterals,
  ContractSwitchTypeWithLiterals,
  ContractSwitchReasonWithLiterals,
  PriceIncreaseTriggerWithLiterals,
  ProductAdjustmentWithLiterals,
  FieldToUpdateWithLiterals,
  WebhookIdentityTypeWithLiterals,
  CommonQueryWithEntityContext,
  PackageQuery,
} from './premium-reseller-v1-packages-packages.universal.js';
